import React, { useEffect, useRef, useState } from "react";
import { Button, Spin } from "antd";
import Image from "next/image";
import styles from "@aspen/theme/downBill.module.less";
import { getReportList, downloadReport } from "@aspen/services";
import type { IReportListItem, IRes } from "@aspen/model";
import { message, WithEmptyContent } from "@aspen/ui";
import { GA_EVENT_NAME, downLoadFileBlob, i18nUtil, reportEvent } from "@aspen/libs";
import { ModalFilePreview } from "./ModalFilePreview";

import pdfIcon from "@aspen/assets/images/pdf-icon.png";
import noResultsImg from "@aspen/assets/images/no_results.png";

const PortfolioReportTab: React.FC = () => {
  const intl = i18nUtil.t();
  const FILE_NAME = intl["download.portfolio.report.filename.suffix"];

  const [loading, setLoading] = useState<boolean>(false);
  const [currentButtonIndex, setCurrentButtonIndex] = useState<number | null>();
  const [isDownload, setIsDownload] = useState<boolean>(false);
  const [documentList, setDocumentList] = useState<IReportListItem[]>([]);
  const [previewFile, setPreviewFile] = useState<IReportListItem | null>(null);
  const nextTokenRef = useRef<string>("");

  // 响应处理函数 & 分页
  const handleListResponse = (loadMore: boolean) => (res: IRes) => {
    if (res?.code === "0") {
      const { data } = res;
      const { rows = [], nextToken } = data;
      nextTokenRef.current = nextToken;
      setDocumentList(prev => (loadMore ? prev.concat(rows) : data?.rows));
    } else {
      message.error(intl?.[res?.msg] ?? res?.msg);
    }
  };

  const handleError = err => {
    message.error(intl?.[err?.msg] ?? err?.msg);
  };

  const handleDownloadBlob = (res: any, report: IReportListItem) => {
    const _date = report?.reportDate?.split("-");
    let fileName = "";
    if (_date[0] && _date[1]) {
      fileName = `${FILE_NAME} (${_date[0]}${_date[1]}).pdf`;
    } else {
      fileName = FILE_NAME;
    }
    downLoadFileBlob(res, "application/pdf", fileName);
  };

  const resetButtonState = () => {
    setIsDownload(false);
    setCurrentButtonIndex(null);
  };

  // 获取账单列表
  const fetchReportList = (
    params: { limit: number; nextToken?: string } = { limit: 12 },
    loadMore = false,
  ) => {
    setLoading(true);
    getReportList(params)
      .then(handleListResponse(loadMore))
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  // 下载账单
  const handleDownload = (report: IReportListItem, index: number) => {
    reportEvent({
      moduleName: GA_EVENT_NAME.portfolioReport.download,
      detailParams: { date: report?.reportDate },
    });
    setCurrentButtonIndex(index);
    setIsDownload(true);
    void downloadReport({
      reportDate: report.reportDate,
      reportUrl: report.pdfUrl,
    })
      .then(res => handleDownloadBlob(res, report))
      .finally(resetButtonState);
  };

  useEffect(() => {
    fetchReportList();
  }, []);
  const handleName = (date: string) => {
    const _date = date?.split("-");
    let nameStr;
    if (_date[0] && _date[1]) {
      nameStr = `${intl["download.portfolio.report"]}-${_date[0]}.${_date[1]}`;
    } else {
      nameStr = intl["download.portfolio.report"];
    }
    return nameStr;
  };

  const preview = (report: IReportListItem) => {
    reportEvent({
      moduleName: GA_EVENT_NAME.portfolioReport.preview,
      detailParams: { date: report?.reportDate },
    });
    setPreviewFile(report);
  };

  const _renderDocumentList = () => {
    return documentList.map((item, index) => (
      <div key={index} className={styles.billItem}>
        <div className={styles.flexRow}>
          <Image unoptimized src={pdfIcon} alt="" width="27" height="27" />
          <span className={styles.billName}>{handleName(item?.reportDate)}</span>
        </div>
        {item?.pdfUrl ? (
          <div className={`${styles.flexRow}`}>
            <Button
              className={styles.preview}
              onClick={() => {
                preview(item);
              }}>
              <span>{intl["download.portfolio.report.preview"]}</span>
            </Button>
            <Button
              loading={currentButtonIndex === index ? isDownload : false}
              className={styles.download}
              onClick={() => {
                handleDownload(item, index);
              }}>
              <span> {intl["button.download"]}</span>
            </Button>
          </div>
        ) : null}
      </div>
    ));
  };

  return (
    <div className={styles.myInvestmentPlan} style={{ marginTop: 0 }}>
      <div className={styles.myInvestmentPlanArea}>
        <WithEmptyContent showEmpty>
          <Spin spinning={loading}>
            <div className={styles.wrapper}>
              {documentList.length ? (
                _renderDocumentList()
              ) : (
                <div className={styles.img}>
                  <Image alt="" src={noResultsImg} width={240} height={180} />
                </div>
              )}
            </div>
          </Spin>
        </WithEmptyContent>
        {nextTokenRef?.current ? (
          <div
            className={styles.loadMore}
            onClick={() => {
              reportEvent({
                moduleName: GA_EVENT_NAME.portfolioReport.loadMore,
              });
              fetchReportList({ limit: 12, nextToken: nextTokenRef.current }, true);
            }}>
            {intl["button.loadMore"]}
          </div>
        ) : null}
      </div>
      {/* 文件预览弹窗 */}
      {previewFile && (
        <ModalFilePreview
          previewFile={{
            url: previewFile.pdfUrl,
            title: `${handleName(previewFile.reportDate)}.pdf`,
          }}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </div>
  );
};

export { PortfolioReportTab };
